import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, Trophy, Star, Flame, Crown, Gift, Clock, MessageCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export function TopFanBadge({ rank, size = 'default' }) {
  if (!rank || rank > 3) return null;
  
  const badges = {
    1: { label: '#1 Top Fan', color: 'bg-amber-500 text-black', icon: Crown },
    2: { label: '#2 Top Fan', color: 'bg-gray-400 text-black', icon: Trophy },
    3: { label: '#3 Top Fan', color: 'bg-amber-700 text-white', icon: Star }
  };
  
  const badge = badges[rank];
  const Icon = badge.icon;
  
  const sizeClasses = {
    small: 'text-xs px-1.5 py-0.5',
    default: 'text-sm px-2 py-1',
    large: 'text-base px-3 py-1.5'
  };
  
  return (
    <Badge 
      className={cn(sizeClasses[size], badge.color, "flex items-center gap-1")}
      data-testid={`top-fan-badge-${rank}`}
    >
      <Icon className={cn(size === 'small' ? 'w-3 h-3' : 'w-4 h-4')} />
      {badge.label}
    </Badge>
  );
}

export function TopFansList({ artistId, limit = 5 }) {
  const [topFans, setTopFans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (artistId) fetchTopFans();
  }, [artistId]);

  const fetchTopFans = async () => {
    try {
      const response = await fetch(`${API}/artists/${artistId}/top-fans?limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        setTopFans(data.top_fans || []);
      }
    } catch (error) {
      console.error('Error fetching top fans:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (topFans.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No top fans yet. Be the first!</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="top-fans-list">
      {topFans.map((fan) => (
        <div 
          key={fan.user_id}
          className={cn(
            "flex items-center justify-between p-3 rounded-lg",
            fan.rank === 1 ? "bg-amber-500/10 border border-amber-500/30" :
            fan.rank === 2 ? "bg-gray-400/10 border border-gray-400/30" :
            fan.rank === 3 ? "bg-amber-700/10 border border-amber-700/30" :
            "bg-muted/20"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
              fan.rank === 1 ? "bg-amber-500 text-black" :
              fan.rank === 2 ? "bg-gray-400 text-black" :
              fan.rank === 3 ? "bg-amber-700 text-white" :
              "bg-muted text-muted-foreground"
            )}>
              {fan.rank}
            </div>
            <div>
              <p className="font-medium text-sm">{fan.user_name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Gift className="w-3 h-3" />
                  {fan.coins_gifted.toLocaleString()}
                </span>
                {fan.tips_usd > 0 && (
                  <span>${fan.tips_usd.toFixed(2)} tips</span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-amber-400">{Math.round(fan.score).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">points</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TopFansCard({ artistId }) {
  return (
    <Card className="glass-card border-amber-500/30 p-4" data-testid="top-fans-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
          <Trophy className="w-4 h-4 text-amber-400" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-sm">Top Supporters</h3>
          <p className="text-xs text-muted-foreground">Last 30 days</p>
        </div>
      </div>
      <TopFansList artistId={artistId} limit={5} />
    </Card>
  );
}

export function MyFanStatus({ artistId, getAuthHeaders }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (artistId && getAuthHeaders) fetchStatus();
  }, [artistId]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API}/artists/${artistId}/fan-status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setStatus(await response.json());
      }
    } catch (error) {
      console.error('Error fetching fan status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !status) return null;

  return (
    <Card className="glass-card border-border p-4" data-testid="my-fan-status">
      <h4 className="text-sm font-medium mb-3">Your Support Stats</h4>
      
      {status.is_top_fan && (
        <div className="mb-3">
          <TopFanBadge rank={status.rank} />
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2">
          <Gift className="w-4 h-4 text-secondary" />
          <span>{status.coins_gifted.toLocaleString()} coins</span>
        </div>
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-green-400" />
          <span>${status.tips_usd.toFixed(2)} tips</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          <span>{status.watch_minutes} min watched</span>
        </div>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-purple-400" />
          <span>{status.chat_messages} messages</span>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-border">
        <p className="text-xs text-muted-foreground">
          Score: <span className="text-amber-400 font-semibold">{Math.round(status.score).toLocaleString()}</span> points
        </p>
      </div>
    </Card>
  );
}
